import { useEffect, useRef } from 'react';
import { clipPlayer, onPlayerFrame, playerReady, releasePlayers } from '../../render/video';
import { isActive, pageDuration, pageVideos, sourceTime } from '../../model/timeline';

// Seconds a playing clip may drift from the page's clock before it is put back in place.
const DRIFT = 0.25;
const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const raf = callback => (typeof requestAnimationFrame === 'function' ? requestAnimationFrame(callback) : setTimeout(() => callback(now()), 16));
const cancel = id => (typeof cancelAnimationFrame === 'function' ? cancelAnimationFrame(id) : clearTimeout(id));

// The page's playback (2.13.0): its clock (`time`, seconds on the page's timeline), whether
// it plays, and one <video> per clip kept in step with it. Playing, the clock runs in real
// time and every clip plays while it is on; paused, each clip is seeked to the frame the
// playhead shows. The stage paints `frame(layer)` (a clip's player once it has that frame,
// else nothing: the renderer paints its still) on every `subscribe` tick; the timeline reads
// `getState()`. `muted` silences the preview, never the file.
export function createPlayback() {
  let state = { time: 0, playing: false, duration: 0, muted: false };
  let page = null;
  let frame = 0;
  let clock = null;
  const listeners = new Set();
  const emit = () => listeners.forEach(listener => listener());
  // A clip's player with a new frame (loaded, seeked) is a tick too.
  let unlisten = null;
  const listen = () => { if (!unlisten) unlisten = onPlayerFrame(emit); };

  const sync = () => {
    pageVideos(page).forEach(layer => {
      const player = clipPlayer(layer);
      if (!player) return;
      const on = layer.visible && isActive(layer, state.time);
      const target = sourceTime(layer, state.time);
      player.muted = state.muted || layer.muted;
      player.volume = layer.volume;
      if (state.playing && on) {
        if (Math.abs(player.currentTime - target) > DRIFT) player.currentTime = target;
        if (player.paused) player.play()?.catch?.(() => {});
        return;
      }
      if (!player.paused) player.pause();
      if (on && Math.abs(player.currentTime - target) > 0.02) player.currentTime = target;
    });
  };
  const tick = at => {
    if (!state.playing) return;
    const time = clock.from + (at - clock.at) / 1000;
    if (time >= state.duration) { state = { ...state, time: state.duration, playing: false }; sync(); emit(); return; }
    state = { ...state, time };
    sync();
    emit();
    frame = raf(tick);
  };

  const store = {
    getState: () => state,
    subscribe: listener => { listeners.add(listener); return () => listeners.delete(listener); },
    // The page on the stage: another page starts paused at 0; the same one (edited) keeps its time.
    setPage: next => {
      listen();
      const turned = next?.id !== page?.id;
      page = next || null;
      releasePlayers(pageVideos(page).map(layer => layer.id));
      const duration = pageDuration(page);
      if (turned || !duration) { cancel(frame); state = { ...state, playing: false, time: 0, duration }; }
      else state = { ...state, duration, time: Math.min(state.time, duration) };
      sync();
      emit();
    },
    play: () => {
      if (!state.duration || state.playing) return;
      const from = state.time >= state.duration - 0.05 ? 0 : state.time;
      clock = { from, at: now() };
      state = { ...state, playing: true, time: from };
      sync();
      emit();
      frame = raf(tick);
    },
    pause: () => {
      cancel(frame);
      if (!state.playing) return;
      state = { ...state, playing: false };
      sync();
      emit();
    },
    toggle: () => (state.playing ? store.pause() : store.play()),
    seek: time => {
      const next = Math.max(0, Math.min(state.duration, time));
      if (state.playing) clock = { from: next, at: now() };
      state = { ...state, time: next };
      sync();
      emit();
    },
    setMuted: muted => { state = { ...state, muted }; sync(); emit(); },
    frame: layer => { const player = clipPlayer(layer); return playerReady(player) ? player : null; },
    dispose: () => { cancel(frame); state = { ...state, playing: false }; releasePlayers([]); unlisten?.(); unlisten = null; },
  };
  return store;
}

// The editor's playback for the page on the stage (null while there is none).
export default function usePlayback(page) {
  const ref = useRef(null);
  if (!ref.current) ref.current = createPlayback();
  useEffect(() => { ref.current.setPage(page); }, [page]);
  useEffect(() => () => ref.current.dispose(), []);
  return ref.current;
}
